/**
 * useBulkExport Hook
 *
 * Hook for handling bulk exports across all FPs in a region
 * Similar to useAsyncExport but posts to bulk endpoint
 *
 * Flow:
 * 1. POST to /api/lightspeed/bulk/{export_type}/export/async - returns job_id
 * 2. Poll GET /api/lightspeed/{full_export_type}/export/status/{job_id} every 3 seconds
 * 3. When complete, download from /api/lightspeed/{full_export_type}/export/download/{job_id}
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { downloadFile } from '../services/api'

interface ProgressUpdate {
  timestamp: string
  message: string
  current_count?: number
  total_count?: number
  percentage?: number
}

interface FpError {
  fp_code: string
  error: string
}

interface BulkExportState {
  loading: boolean
  error: string | null
  success: string | null
  warning: string | null
  progressMessage: string
  currentCount: number
  totalCount: number
  percentage: number | null
  fpErrors: FpError[] | null
}

interface UseBulkExportOptions {
  exportType: string  // e.g., 'ls_sales' (will be prefixed with 'bulk_')
  onComplete?: (filename: string, totalRecords: number) => void
  onError?: (error: string) => void
  onProgress?: (update: ProgressUpdate) => void
}

interface StartBulkExportParams {
  region: 'US' | 'ANZ'
  date_from?: string
  date_to?: string
}

export function useBulkExport(options: UseBulkExportOptions) {
  const { exportType, onComplete, onError, onProgress } = options

  // Full export type includes 'bulk_' prefix
  const fullExportType = exportType.startsWith('bulk_') ? exportType : `bulk_${exportType}`

  const [state, setState] = useState<BulkExportState>({
    loading: false,
    error: null,
    success: null,
    warning: null,
    progressMessage: '',
    currentCount: 0,
    totalCount: 0,
    percentage: null,
    fpErrors: null
  })

  const pollingIntervalRef = useRef<number | null>(null)
  const jobIdRef = useRef<string | null>(null)
  const downloadingRef = useRef<boolean>(false)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      downloadingRef.current = false
    }
  }, [])

  const updateProgress = useCallback((update: ProgressUpdate) => {
    setState(prev => ({
      ...prev,
      progressMessage: update.message,
      currentCount: update.current_count ?? prev.currentCount,
      totalCount: update.total_count ?? prev.totalCount,
      percentage: update.percentage ?? prev.percentage
    }))

    if (onProgress) {
      onProgress(update)
    }
  }, [onProgress])

  const handleError = useCallback((errorMessage: string) => {
    setState(prev => ({
      ...prev,
      loading: false,
      error: errorMessage,
      progressMessage: ''
    }))

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    downloadingRef.current = false

    if (onError) {
      onError(errorMessage)
    }
  }, [onError])

  const downloadExport = useCallback(async (jobId: string) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    const getApiBaseUrl = () => {
      return API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
    }

    try {
      const downloadUrl = `${getApiBaseUrl()}/lightspeed/${fullExportType}/export/download/${jobId}`
      const response = await fetch(downloadUrl, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`)
      }

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/)
      const filename = filenameMatch ? filenameMatch[1] : `bulk_export_${jobId}.csv`

      // Download the file
      const blob = await response.blob()
      downloadFile(blob, filename)

      return { filename, blob }
    } catch (error) {
      console.error('Download failed:', error)
      throw new Error(`Failed to download export: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [fullExportType])

  const watchProgress = useCallback((jobId: string) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    const getApiBaseUrl = () => {
      return API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
    }

    // Use full export type for status URL
    const statusUrl = `${getApiBaseUrl()}/lightspeed/${fullExportType}/export/status/${jobId}`

    // Poll status endpoint every 3 seconds
    const pollStatus = async () => {
      try {
        const response = await fetch(statusUrl, {
          credentials: 'include'
        })

        if (!response.ok) {
          if (response.status === 404) {
            handleError('Export job not found or expired')
            return
          } else if (response.status === 504 || response.status === 502) {
            console.log('Status check timed out, will retry on next poll')
            return
          } else {
            handleError(`Failed to check status: ${response.statusText}`)
            return
          }
        }

        const data = await response.json()

        // Update progress if we have a latest message
        if (data.latest_message) {
          updateProgress({
            timestamp: new Date().toISOString(),
            message: data.latest_message,
            current_count: data.current_count,
            total_count: data.total_count,
            percentage: data.percentage
          })
        }

        // Check if completed
        if (data.status === 'completed') {
          if (downloadingRef.current) {
            console.log('Download already in progress, skipping')
            return
          }
          downloadingRef.current = true

          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }

          setState(prev => ({
            ...prev,
            progressMessage: 'Download starting...'
          }))

          try {
            const { filename } = await downloadExport(jobId)

            setState(prev => ({
              ...prev,
              loading: false,
              success: `Bulk export completed successfully! File: ${filename}`,
              progressMessage: '',
              currentCount: 0,
              totalCount: 0,
              percentage: null,
              fpErrors: data.fp_errors || null
            }))

            if (onComplete) {
              onComplete(filename, data.total_records || 0)
            }
          } catch (downloadError) {
            handleError(downloadError instanceof Error ? downloadError.message : 'Download failed')
          } finally {
            downloadingRef.current = false
          }

          return
        }

        // Check if failed
        if (data.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }

          handleError(data.error || 'Bulk export failed')
          return
        }

      } catch (error) {
        console.error('Polling error:', error)
      }
    }

    // Initial poll
    pollStatus()

    // Set up polling interval (every 3 seconds)
    pollingIntervalRef.current = setInterval(pollStatus, 3000)

  }, [fullExportType, updateProgress, handleError, downloadExport, onComplete])

  const startExport = useCallback(async (params: StartBulkExportParams) => {
    // Stop any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    downloadingRef.current = false

    // Reset state
    setState({
      loading: true,
      error: null,
      success: null,
      warning: null,
      progressMessage: `Starting bulk ${params.region} export...`,
      currentCount: 0,
      totalCount: 0,
      percentage: null,
      fpErrors: null
    })

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    const getApiBaseUrl = () => {
      return API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
    }

    try {
      // Step 1: Start the bulk export job
      const startUrl = `${getApiBaseUrl()}/lightspeed/bulk/${exportType}/export/async`

      // Build FormData from params
      const formData = new FormData()
      formData.append('region', params.region)
      formData.append('format', 'csv')

      if (params.date_from) {
        formData.append('date_from', params.date_from)
      }
      if (params.date_to) {
        formData.append('date_to', params.date_to)
      }

      const response = await fetch(startUrl, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(errorData.detail || `Failed to start bulk export: ${response.statusText}`)
      }

      const jobData = await response.json()
      const jobId = jobData.job_id

      if (!jobId) {
        throw new Error('No job ID returned from server')
      }

      jobIdRef.current = jobId

      setState(prev => ({
        ...prev,
        progressMessage: 'Bulk export job started, polling for status updates...'
      }))

      // Step 2: Start polling for progress
      watchProgress(jobId)

    } catch (error) {
      console.error('Failed to start bulk export:', error)
      handleError(error instanceof Error ? error.message : 'Failed to start bulk export')
    }
  }, [exportType, watchProgress, handleError])

  const cancel = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    downloadingRef.current = false

    setState({
      loading: false,
      error: null,
      success: null,
      warning: null,
      progressMessage: '',
      currentCount: 0,
      totalCount: 0,
      percentage: null,
      fpErrors: null
    })

    jobIdRef.current = null
  }, [])

  return {
    ...state,
    startExport,
    cancel,
    jobId: jobIdRef.current
  }
}
